/**
 * premiumPreview.service.ts  (frontend)
 *
 * Calls POST /api/preview/start after the user's first successful log.
 * After a successful start, invalidates the subscription cache so AppContext
 * refreshes entitlement immediately and the preview banner appears without reload.
 */

import { getFirebaseIdToken } from '../utils/firebaseToken';
import { env } from '../utils/env';
import { loggedFetch } from '../utils/apiLogger';
import { invalidateSubscriptionCache } from './stripe.service';

export interface PreviewStartResponse {
  started: boolean;
  expiresAt?: string;
  reason?: string;
}

export async function startPremiumPreview(): Promise<PreviewStartResponse> {
  const token = await getFirebaseIdToken();

  const response = await loggedFetch(`${env.apiBaseUrl}/api/preview/start`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    // 403 = unverified — not an error, just blocked
    if (response.status === 403) {
      return { started: false, reason: 'unverified' };
    }
    throw new Error(body.error || `preview/start HTTP ${response.status}`);
  }

  const result = await response.json() as PreviewStartResponse;

  if (result.started) {
    // Invalidate subscription cache so AppContext fetches fresh entitlement
    invalidateSubscriptionCache();
  }

  return result;
}

export interface ActivateAfterVerificationResponse {
  status: 'activated' | 'already_active' | 'already_used' | 'not_pending' | 'unverified' | 'not_found' | 'error';
  isPremium?: boolean;
  previewActive?: boolean;
  premiumPreviewExpiresAt?: string | null;
  premiumPreviewUsed?: boolean;
  plan?: string;
  entitlementReason?: string;
}

export async function activatePreviewAfterVerification(): Promise<ActivateAfterVerificationResponse> {
  const token = await getFirebaseIdToken();

  const response = await loggedFetch(`${env.apiBaseUrl}/api/preview/activate-after-verification`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.error || `preview/activate-after-verification HTTP ${response.status}`);
  }

  return await response.json() as ActivateAfterVerificationResponse;
}
